<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/style/useImportType: Vue registers this component from the template.
import EntrButton from './EntrButton.vue';

withDefaults(
  defineProps<{
    secondsRemaining?: number;
  }>(),
  { secondsRemaining: 0 }
);

const emit = defineEmits<{
  continue: [];
}>();

const { t } = useI18n();
const buttonRef = ref<InstanceType<typeof EntrButton> | null>(null);

function continueSession(): void {
  emit('continue');
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== 'Escape') return;
  event.preventDefault();
  continueSession();
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  const buttonElement = buttonRef.value?.$el as HTMLButtonElement | undefined;
  buttonElement?.focus();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div class="idle-backdrop" role="dialog" aria-modal="true" aria-labelledby="idle-heading">
    <section class="idle-modal">
      <h2 id="idle-heading" class="t-headline">{{ t('idleModal.heading') }}</h2>
      <p class="countdown t-body-lg">
        {{ t('idleModal.countdownLabel', { seconds: secondsRemaining }) }}
      </p>
      <EntrButton ref="buttonRef" class="continue" @click="continueSession">
        {{ t('idleModal.continueButton') }}
      </EntrButton>
    </section>
  </div>
</template>

<style scoped>
.idle-backdrop {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(14, 18, 69, 0.72);
  backdrop-filter: blur(8px);
  pointer-events: auto;
}

.idle-modal {
  width: min(720px, calc(100% - 128px));
  padding: 64px;
  border: 2px solid color-mix(in srgb, var(--entr-text) 20%, transparent);
  border-radius: var(--entr-radius-sheet);
  background: var(--entr-ink);
  color: var(--entr-text);
  text-align: center;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.42);
}

.countdown {
  margin-top: 24px;
  color: var(--entr-text-soft);
}

.continue {
  margin-top: 56px;
  min-width: 360px;
  height: 104px;
  font-size: 34px;
}

.idle-modal-enter-active,
.idle-modal-leave-active {
  transition: opacity 180ms ease;
}

.idle-modal-enter-active .idle-modal,
.idle-modal-leave-active .idle-modal {
  transition: transform 200ms ease;
}

.idle-modal-enter-from,
.idle-modal-leave-to {
  opacity: 0;
}

.idle-modal-enter-from .idle-modal,
.idle-modal-leave-to .idle-modal {
  transform: translateY(16px) scale(0.98);
}
</style>
